import axios from 'axios';
import { API_ROOT, logApiError } from '@/store/api';
import { getTrackingDistinctId } from '@/posthog_tracking';

/**
 * Report PDFs are rendered server-side, so the backend emits the
 * `report_generated` PostHog event (ENG-1965). Passing this browser's distinct
 * id lets that event join the visitor's other activity instead of landing in
 * the shared "anonymous" bucket.
 *
 * The key is omitted entirely when there is no id. On the GET-based reports the
 * payload becomes URLSearchParams, which serializes null as the *string*
 * "null" — the backend would take that for a real distinct id and pile every
 * such visitor onto one "null" person, which is the exact failure this is
 * meant to prevent.
 */
const withTrackingIdentity = payload => {
    const distinctId = getTrackingDistinctId();
    return distinctId ? { ...payload, posthog_distinct_id: distinctId } : { ...payload };
};

export default {
    namespaced: true,
    actions: {
        async generateComparisonCapGainsReport({ dispatch }, payload) {
            const baseUrl = `${API_ROOT}/reports/custom/capital-gains-comparison/`;
            const params = new URLSearchParams();
            const query = withTrackingIdentity(payload);
            for (const key in query) {
                const value = query[key];
                if (Array.isArray(value)) {
                    value.forEach(val => params.append(key, val));
                } else {
                    params.append(key, value);
                }
            }
            const fullUrl = `${baseUrl}?${params.toString()}`;

            try {
                // window.open returns null when the popup is blocked and does
                // not throw, so the handle is the only evidence anything
                // happened. This report is delivered by opening a tab, so that
                // is as close to success as this surface can observe — it does
                // not confirm the PDF rendered. The authoritative signal is the
                // backend's report_generated event.
                const opened = window.open(fullUrl, '_blank');
                return Boolean(opened);
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async generateCrutReport({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${API_ROOT}/reports/crut/full/`, withTrackingIdentity(payload));

                return { data };
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async generateQsbsReport({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${API_ROOT}/reports/bespoke/qsbs/`, withTrackingIdentity(payload));

                return { data };
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async generateEstateReport({ dispatch }, payload) {
            try {
                const { data } = await axios.post(
                    `${API_ROOT}/reports/estate/full-email/`,
                    withTrackingIdentity(payload),
                );

                return { data };
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async downloadEstateReport({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${API_ROOT}/reports/estate/full/`, withTrackingIdentity(payload), {
                    responseType: 'blob',
                });
                const url = URL.createObjectURL(data);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'valur-estate-tax-plan.pdf';
                link.click();
                URL.revokeObjectURL(url);

                return true;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async generateSolarReport({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${API_ROOT}/reports/solar/full/`, withTrackingIdentity(payload));

                return { data };
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
    },
};
